// ==================== REGISTER PAGE ====================

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  User, 
  Building2, 
  CheckCircle2,
  Sparkles,
  Clock,
  ShieldCheck,
  AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Checkbox } from '../../components/ui/Checkbox';
import { ROUTES } from '../../constants';
import { useAuth } from '../../contexts';
import { registerFormSchema, type RegisterFormData } from '../../utils/form-validations';
import { fadeInUp, staggerContainer } from '../../utils/animations';

// ==================== CONSTANTS ====================

const REGISTER_BENEFITS = [
  {
    icon: Sparkles,
    title: 'Powerful Automation',
    description: 'Automate your WhatsApp campaigns effortlessly',
  },
  {
    icon: Clock,
    title: 'Quick Setup',
    description: 'Get started in minutes with our intuitive platform',
  },
  {
    icon: ShieldCheck,
    title: 'Secure & Reliable',
    description: 'Enterprise-grade security for your data',
  },
];

// ==================== MAIN COMPONENT ====================

const RegisterPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register: registerUser, error, clearError, isLoading } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      companyName: '',
      phone: '',
      agreeToTerms: false,
    },
  });

  const password = watch('password', '');

  const passwordRequirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { met: /[a-z]/.test(password), text: 'One lowercase letter' },
    { met: /[0-9]/.test(password), text: 'One number' },
    { met: /[@$!%*?&]/.test(password), text: 'One special character (@$!%*?&)' },
  ];

  const onSubmit = async (data: RegisterFormData) => {
    clearError();
    
    const result = await registerUser({
      email: data.email,
      password: data.password,
      firstName: data.firstName,
      lastName: data.lastName,
      businessName: data.companyName,
    });

    if (result.success) {
      // Navigate to email verification page with email in state
      navigate(ROUTES.AUTH.VERIFY_EMAIL, { state: { email: data.email } });
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="animate-fade-in"
    >
      {/* Header */}
      <motion.div variants={fadeInUp} className="text-center mb-6">
        <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white mb-2">
          Create Your Account
        </h1>
        <p className="text-(--color-muted-gray)">
          Start your journey with AmplifyMsg
        </p>
      </motion.div>

      {/* Benefits - Mobile Hidden, Desktop Shown on side */}
      <motion.div 
        variants={fadeInUp}
        className="hidden lg:grid lg:grid-cols-3 gap-3 mb-6"
      >
        {REGISTER_BENEFITS.map((benefit, index) => (
          <div
            key={index}
            className="flex items-start gap-3 p-3 rounded-lg bg-(--color-navy-light)/50 border border-(--color-border-gray)"
          >
            <benefit.icon size={18} className="text-(--color-signal-orange) shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-white">{benefit.title}</p>
              <p className="text-xs text-(--color-muted-gray)">{benefit.description}</p>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* General Error */}
        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -10, height: 0 }}
              transition={{ duration: 0.3 }}
              className="flex items-start gap-3 p-4 rounded-md bg-red-500/10 border border-red-500/30"
              role="alert"
              aria-live="assertive"
            >
              <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-500">Registration Failed</p>
                <p className="text-sm text-red-400 mt-0.5">{error}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Name Fields */}
        <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-4">
          <Input
            label="First Name"
            placeholder="John"
            error={errors.firstName?.message}
            leftIcon={<User size={18} />}
            {...register('firstName')}
          />
          <Input
            label="Last Name"
            placeholder="Doe"
            error={errors.lastName?.message}
            {...register('lastName')}
          />
        </motion.div>

        {/* Email */}
        <motion.div variants={fadeInUp}>
          <Input
            label="Email Address"
            type="email"
            placeholder="john@company.com"
            error={errors.email?.message}
            leftIcon={<Mail size={18} />}
            autoComplete="email"
            {...register('email')}
          />
        </motion.div>

        {/* Company Name */}
        <motion.div variants={fadeInUp}>
          <Input
            label="Company / Organization Name"
            placeholder="Your Company Ltd."
            error={errors.companyName?.message}
            leftIcon={<Building2 size={18} />}
            {...register('companyName')}
          />
        </motion.div>

        {/* Password */}
        <motion.div variants={fadeInUp}>
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Create a strong password"
            error={errors.password?.message}
            leftIcon={<Lock size={18} />}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            }
            autoComplete="new-password"
            {...register('password')}
          />
          
          {/* Password Requirements */}
          {password && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              {passwordRequirements.map((req, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-2 text-xs ${
                    req.met ? 'text-(--color-success)' : 'text-(--color-muted-gray)'
                  }`}
                >
                  <CheckCircle2 size={12} />
                  {req.text}
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Confirm Password */}
        <motion.div variants={fadeInUp}>
          <Input
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm your password"
            error={errors.confirmPassword?.message}
            leftIcon={<Lock size={18} />}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="hover:text-white transition-colors"
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            }
            autoComplete="new-password"
            {...register('confirmPassword')}
          />
        </motion.div>

        {/* Terms Checkbox */}
        <motion.div variants={fadeInUp}>
          <Checkbox
            label={
              <span className="text-sm text-(--color-soft-gray)">
                I agree to the{' '}
                <Link to={ROUTES.TERMS} className="text-(--color-signal-orange) hover:underline">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to={ROUTES.PRIVACY} className="text-(--color-signal-orange) hover:underline">
                  Privacy Policy
                </Link>
              </span>
            }
            error={errors.agreeToTerms?.message}
            {...register('agreeToTerms')}
          />
        </motion.div>

        {/* Submit Button */}
        <motion.div variants={fadeInUp}>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            isLoading={isLoading}
            className="mt-2"
          >
            Create Account
          </Button>
        </motion.div>
      </form>

      {/* Divider */}
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-(--color-border-gray)" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-(--color-midnight-navy) text-(--color-muted-gray)">
            or
          </span>
        </div>
      </div>

      {/* Login Link */}
      <p className="text-center text-sm text-(--color-muted-gray)">
        Already have an account?{' '}
        <Link
          to={ROUTES.AUTH.LOGIN}
          className="text-(--color-signal-orange) hover:text-(--color-orange-light) font-medium transition-colors"
        >
          Sign in
        </Link>
      </p>
    </motion.div>
  );
};

export { RegisterPage };
